import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import DashboardLayout from '../Layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../Common/LoadingSpinner';
import { requestHelpers } from '../../services/api';
import './Analytics.css';

const Analytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [selectedMetric, setSelectedMetric] = useState('overview');
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    fetchAnalytics();

    // Setup Socket.io for real-time updates
    const socketBase = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    const socket = io(socketBase, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    if (user?._id) {
      socket.emit('join', user._id);
      console.log('Joined socket room:', user._id);
    }

    socket.on('campaign_update', (data) => {
      console.log('Campaign update received:', data);
      fetchAnalytics(); // Refresh data on update
    });

    return () => {
      socket.disconnect();
    };
  }, [dateRange, user?._id]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const response = await requestHelpers.get(`/analytics/dashboard`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });

      const { overview, timeSeries, topPerforming } = response.data || {};

        const transformedAnalytics = {
          overview: {
            totalCampaigns: overview?.campaigns?.totalCampaigns || 0,
            totalMessages: overview?.messages?.outboundMessages || 0,
            deliveredMessages: overview?.messages?.deliveredMessages || 0,
            readMessages: overview?.messages?.readMessages || 0,
            failedMessages: overview?.messages?.failedMessages || 0,
            deliveryRate: overview?.messages?.deliveryRate || 0,
            readRate: overview?.messages?.readRate || 0,
            totalContacts: overview?.contacts?.totalContacts || 0,
            activeContacts: overview?.contacts?.activeContacts || 0,
            unsubscribedContacts: overview?.contacts?.unsubscribedContacts || 0,
            interestedContacts: overview?.contacts?.interestedContacts || 0,
            activeFlows: overview?.flows?.activeFlows || 0
          },
          spendAnalysis: overview?.spendBreakdown || [],
          campaignMetrics: (topPerforming?.campaigns || []).map(c => ({
            id: c._id,
            name: c.name,
            sent: c.analytics?.messagesSent || 0,
            delivered: c.analytics?.messagesDelivered || 0,
            read: c.analytics?.messagesRead || 0,
            responses: c.analytics?.messagesReplied || 0,
            clicks: c.analytics?.clicks || 0,
            conversions: c.analytics?.conversions || 0,
            deliveryRate: c.analytics?.messagesSent > 0 ? (c.analytics.messagesDelivered / c.analytics.messagesSent * 100) : 0,
            readRate: c.analytics?.messagesDelivered > 0 ? (c.analytics.messagesRead / c.analytics.messagesDelivered * 100) : 0,
            responseRate: c.analytics?.messagesDelivered > 0 ? (c.analytics.messagesReplied / c.analytics.messagesDelivered * 100) : 0,
          })),
          timelineData: (timeSeries || []).map(day => ({
            date: `${day._id.year}-${String(day._id.month).padStart(2, '0')}-${String(day._id.day).padStart(2, '0')}`,
            sent: day.messagesSent,
            delivered: day.messagesDelivered,
            read: day.messagesRead || 0,
            responses: day.messagesReceived
          })),
          topPerformingTemplates: (topPerforming?.templates || []).map(t => ({
            id: t._id,
            name: t.name,
            usage: t.usageCount || 0,
            readRate: t.readRate || 0,
            deliveryRate: t.deliveryRate || 0,
            responseRate: t.responseRate || 0
          }))
        };

        setAnalytics(transformedAnalytics);
        setChartData(transformedAnalytics.timelineData);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (num) => {
    if (typeof num !== 'number') return '0.0%';
    return `${num.toFixed(1)}%`;
  };

  const getMetricColor = (value, type = 'percentage') => {
    if (type === 'percentage') {
      if (value >= 80) return '#28a745';
      if (value >= 60) return '#ffc107';
      return '#dc3545';
    }
    return '#667eea';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  if (!analytics) {
    return (
      <DashboardLayout>
        <div className="error-container">
          <p>Failed to load analytics data</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="analytics-page">
        <div className="page-header">
          <div className="header-content">
            <h1>Analytics</h1>
            <p>Track your campaign performance and engagement metrics</p>
            {user?.whatsappNumber && (
              <div className="header-number-badge mt-2" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                <i className="fab fa-whatsapp"></i>
                <span>Linked to: {user.whatsappNumber}</span>
              </div>
            )}
          </div>
          <div className="header-actions">
            <div className="date-range-selector">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="date-input"
              />
              <span className="date-separator">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="date-input"
              />
            </div>
            <button className="btn btn-secondary">
              <i className="fas fa-download"></i>
              Export Report
            </button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="analytics-overview">
          <div className="overview-cards">
            <div className="overview-card">
              <div className="card-icon campaigns">
                <i className="fas fa-bullhorn"></i>
              </div>
              <div className="card-content">
                <h3>{formatNumber(analytics.overview.totalCampaigns)}</h3>
                <p>Total Campaigns</p>
                <small className="trend positive">
                  <i className="fas fa-arrow-up"></i> +12% from last month
                </small>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon messages">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="card-content">
                <h3>{formatNumber(analytics.overview.totalMessages)}</h3>
                <p>Messages Sent</p>
                <small className="trend positive">
                  <i className="fas fa-arrow-up"></i> +18% from last month
                </small>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon delivery">
                <i className="fas fa-eye"></i>
              </div>
              <div className="card-content">
                <h3>{formatPercentage(analytics.overview.readRate)}</h3>
                <p>Overall Read Rate</p>
                <small className="trend positive">
                  <i className="fas fa-arrow-up"></i> +2.1% from last month
                </small>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon engagement">
                <i className="fas fa-users"></i>
              </div>
              <div className="card-content">
                <h3>{formatNumber(analytics.overview.totalContacts)}</h3>
                <p>Total Reach</p>
                <small className="trend positive">
                  <i className="fas fa-arrow-up"></i> +15 new today
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Tabs */}
        <div className="analytics-tabs">
          <div className="tab-buttons">
            <button
              className={`tab-btn ${selectedMetric === 'overview' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('overview')}
            >
              Overview
            </button>
            <button
              className={`tab-btn ${selectedMetric === 'campaigns' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('campaigns')}
            >
              Campaigns
            </button>
            <button
              className={`tab-btn ${selectedMetric === 'contacts' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('contacts')}
            >
              Contacts
            </button>
            <button
              className={`tab-btn ${selectedMetric === 'templates' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('templates')}
            >
              Templates
            </button>
          </div>

          <div className="tab-content">
            {selectedMetric === 'overview' && (
              <div className="overview-tab">
                <div className="analytics-grid">


                  {/* Investment Analysis */}
                  <div className="analytics-card">
                    <div className="card-header">
                      <h3>Investment Breakdown</h3>
                      <i className="fas fa-wallet text-muted"></i>
                    </div>
                    <div className="spend-analysis">
                      <div className="total-spend-pill">
                        <span className="label">Total spend in period</span>
                        <span className="value">₹{analytics.spendAnalysis.reduce((acc, curr) => acc + curr.total, 0).toFixed(2)}</span>
                      </div>
                      <div className="spend-categories">
                        {analytics.spendAnalysis.map((item, idx) => (
                          <div key={idx} className="spend-category-row">
                            <div className="category-info">
                              <span className="cat-dot" style={{ background: getMetricColor(80 - (idx * 20), 'sent') }}></span>
                              <span className="cat-name">{item._id.toUpperCase()}</span>
                            </div>
                            <span className="cat-value">₹{item.total.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <p className="gst-notice">* Includes 18% GST where applicable</p>
                    </div>
                  </div>

                  {/* Flow Performance */}
                  <div className="analytics-card">
                    <div className="card-header">
                      <h3>Automation Health</h3>
                      <i className="fas fa-robot text-muted"></i>
                    </div>
                    <div className="health-stats">
                      <div className="health-stat">
                        <span className="val">{analytics.overview.activeFlows}</span>
                        <span className="lbl">Active Flows</span>
                      </div>
                      <div className="health-chart-placeholder">
                        <div className="pulse-dot"></div>
                        <span>Systems Normal</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedMetric === 'campaigns' && (
              <div className="campaigns-tab">
                <div className="campaigns-table-container">
                  <table className="campaigns-analytics-table">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Sent</th>
                        <th>Delivered</th>
                        <th>Read</th>
                        <th>Responses</th>
                        <th>Delivery %</th>
                        <th>Read %</th>
                        <th>Response %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.campaignMetrics.map(campaign => (
                        <tr key={campaign.id}>
                          <td>
                            <div className="campaign-name">{campaign.name}</div>
                            {campaign.buttonClicks && Object.keys(campaign.buttonClicks).length > 0 && (
                              <div className="small text-muted" style={{ fontSize: '0.75rem' }}>
                                {Object.entries(campaign.buttonClicks).map(([b, c]) => `${b.replace(/_/g, '.')}: ${c}`).join(' | ')}
                              </div>
                            )}
                          </td>
                          <td>{formatNumber(campaign.sent)}</td>
                          <td>{formatNumber(campaign.delivered)}</td>
                          <td>{formatNumber(campaign.read)}</td>
                          <td>{formatNumber(campaign.responses)}</td>
                          <td>
                            <span
                              className="metric-badge"
                              style={{ backgroundColor: getMetricColor(campaign.deliveryRate) }}
                            >
                              {formatPercentage(campaign.deliveryRate)}
                            </span>
                          </td>
                          <td>
                            <span
                              className="metric-badge"
                              style={{ backgroundColor: getMetricColor(campaign.readRate) }}
                            >
                              {formatPercentage(campaign.readRate)}
                            </span>
                          </td>
                          <td>
                            <span
                              className="metric-badge"
                              style={{ backgroundColor: getMetricColor(campaign.responseRate) }}
                            >
                              {formatPercentage(campaign.responseRate)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {selectedMetric === 'contacts' && (
              <div className="contacts-tab">
                <div className="contacts-segments">
                  <div className="segment-card">
                    <div className="segment-info">
                      <span className="segment-label">Total Reach</span>
                      <span className="segment-value">{formatNumber(analytics.overview.totalContacts)}</span>
                    </div>
                    <div className="segment-icon total">
                      <i className="fas fa-users"></i>
                    </div>
                  </div>
                  <div className="segment-card">
                    <div className="segment-info">
                      <span className="segment-label">Active Contacts</span>
                      <span className="segment-value">{formatNumber(analytics.overview.activeContacts)}</span>
                    </div>
                    <div className="segment-icon active">
                      <i className="fas fa-user-check"></i>
                    </div>
                  </div>
                  <div className="segment-card">
                    <div className="segment-info">
                      <span className="segment-label">Interested Leads</span>
                      <span className="segment-value highlight">{formatNumber(analytics.overview.interestedContacts)}</span>
                    </div>
                    <div className="segment-icon interested">
                      <i className="fas fa-star"></i>
                    </div>
                  </div>
                  <div className="segment-card">
                    <div className="segment-info">
                      <span className="segment-label">Unsubscribed</span>
                      <span className="segment-value">{formatNumber(analytics.overview.unsubscribedContacts)}</span>
                    </div>
                    <div className="segment-icon unsubscribed">
                      <i className="fas fa-user-slash"></i>
                    </div>
                  </div>
                </div>

                <div className="analytics-card mt-4">
                  <div className="card-header">
                    <h3>Contact Engagement Overview</h3>
                    <p className="text-muted small">Breakdown of how your audience interacts with campaigns</p>
                  </div>
                  <div className="engagement-overview-content">
                    <div className="engagement-summary">
                      <div className="summary-item">
                        <span className="pct">{formatPercentage(analytics.overview.readRate)}</span>
                        <span className="lbl">Response Rate</span>
                      </div>
                      <div className="summary-item">
                        <span className="pct">{formatPercentage(analytics.overview.deliveryRate)}</span>
                        <span className="lbl">Delivery Success</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


            {selectedMetric === 'templates' && (
              <div className="templates-tab">
                <div className="templates-performance">
                  <h3>Top Performing Templates</h3>
                  <div className="templates-grid">
                    {analytics.topPerformingTemplates.map(template => (
                      <div key={template.id} className="template-performance-card">
                        <div className="template-header">
                          <h4>{template.name}</h4>
                          <span className="usage-count">{template.usage} uses</span>
                        </div>
                        <div className="template-metrics">
                          <div className="metric-item">
                            <span className="metric-label">Delivery Rate</span>
                            <span
                              className="metric-value"
                              style={{ color: getMetricColor(template.deliveryRate) }}
                            >
                              {formatPercentage(template.deliveryRate)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Read Rate</span>
                            <span
                              className="metric-value"
                              style={{ color: getMetricColor(template.readRate) }}
                            >
                              {formatPercentage(template.readRate)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Response Rate</span>
                            <span
                              className="metric-value"
                              style={{ color: getMetricColor(template.responseRate) }}
                            >
                              {formatPercentage(template.responseRate)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;